import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from './supabase'

/* ─────────────────────────────────────────────────────────────────────────────
   BENCHMARK PAYMENTS — full rebuild (Aug 2026)
   Corrected constants baked in:
     • McLardy retention  6.75%  (was 6.95%)
     • Attvest            7 business days / 3.5% default  (was 3 / 3.0%)
     • Funding income     charged on BASE PREMIUM, not total invoice
     • Funding rate        read per-policy (cover_data.funding_rate) if present,
                          else the funder default — real rates vary per deal.
   Verify this matches your deployed file and `git diff` before committing.
───────────────────────────────────────────────────────────────────────────── */

const ATTVEST_DAYS = 7
const VICTORY_DAYS = 7
const MMS_SPLIT = 0.0675            // McLardy retention off gross income
const ATTVEST_FUNDING_RATE = 0.035  // default only — real rate is set per policy
const VICTORY_FUNDING_RATE = 0.035  // default only — real rate is set per policy
const MMP_FUNDING_MARGIN = 0.05     // McLardy keeps 5% of GST-inclusive funding comm

// Business-year window + goal
const ANNUAL_TARGET = 500000
const YEAR_START = new Date('2026-04-06')
const YEAR_END = new Date('2027-04-06')

// ─── Date + money helpers ─────────────────────────────────────────────────────
function addBusinessDays(date, days) {
  const result = new Date(date)
  let added = 0
  while (added < days) {
    result.setDate(result.getDate() + 1)
    const dow = result.getDay()
    if (dow !== 0 && dow !== 6) added++
  }
  return result
}

function getPaymentDates() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dates = []
  const year = today.getFullYear()
  const month = today.getMonth()
  const candidates = [
    new Date(year, month, 15),
    new Date(year, month + 1, 1),
    new Date(year, month + 1, 15),
    new Date(year, month + 2, 1),
    new Date(year, month + 2, 15),
  ]
  for (const d of candidates) {
    if (d >= today && dates.length < 3) dates.push(d)
  }
  return dates
}

function getCutoff(paymentDate) {
  const c = new Date(paymentDate)
  c.setDate(c.getDate() - 1)
  c.setHours(23, 59, 59, 999)
  return c
}

function assignToCycle(clearanceDate, paymentDates) {
  const cd = new Date(clearanceDate)
  cd.setHours(0, 0, 0, 0)
  for (let i = 0; i < paymentDates.length; i++) {
    const cutoff = getCutoff(paymentDates[i])
    if (cd <= cutoff) return i
  }
  return paymentDates.length
}

function getPaymentMethod(coverData) {
  const pt = (coverData?.payment_type || '').toLowerCase()
  const pf = (coverData?.premium_funder || '').toLowerCase()
  if (pt === 'monthly' || pt === 'funded') {
    if (pf.includes('attvest')) return 'attvest'
    if (pf.includes('victory')) return 'victory'
    return 'funded'
  }
  return 'bpay'
}

function parseCurr(v) {
  return Number((v || '').toString().replace(/[^\d.]/g, '')) || 0
}

function fmtCurr(n) {
  if (!n || n <= 0) return '$0'
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function fmtCurrDec(n) {
  if (!n) return '$0.00'
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtDateShort(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function daysSince(d) {
  if (!d) return null
  return Math.floor((new Date() - new Date(d)) / 86400000)
}

const PERIOD_MONTHS = { JAN:0, FEB:1, MAR:2, APR:3, MAY:4, JUN:5, JUL:6, AUG:7, SEP:8, OCT:9, NOV:10, DEC:11 }
function parseFundingPeriod(period) {
  if (!period) return null
  const m = period.toString().toUpperCase().match(/([A-Z]{3})[-_]?(\d{2})/)
  if (!m) return null
  const month = PERIOD_MONTHS[m[1]]
  if (month === undefined) return null
  return { year: 2000 + Number(m[2]), month }
}
function isPeriodThisMonth(period) {
  const p = parseFundingPeriod(period)
  if (!p) return false
  const now = new Date()
  return p.year === now.getFullYear() && p.month === now.getMonth()
}

// ─── Money math ───────────────────────────────────────────────────────────────
// Net commission revenue to Benchmark (McLardy): keep (1 - retention) of gross, + GST.
function calcNetRev(d) {
  const basePrem = parseCurr(d.base_premium)
  const commRate = Number(d.comm_rate || 0)
  const fee = parseCurr(d.broker_fee)
  const grossComm = basePrem * (commRate / 100)
  const grossIncome = grossComm + fee
  return (grossIncome * (1 - MMS_SPLIT)) * 1.1
}

// Funding income to Benchmark: premium × rate × 1.045  (×1.1 GST, ×0.95 MMP margin).
// Charged on BASE PREMIUM. Rate is per-policy if stored, else the funder default.
function calcFundingIncome(coverData) {
  const method = getPaymentMethod(coverData)
  const basePrem = parseCurr(coverData.base_premium)
  if (!basePrem) return 0
  const stored = Number(coverData.funding_rate || 0)
  const rate = stored > 0 ? stored
    : method === 'attvest' ? ATTVEST_FUNDING_RATE
    : method === 'victory' ? VICTORY_FUNDING_RATE : 0
  if (!rate) return 0
  return basePrem * rate * 1.1 * (1 - MMP_FUNDING_MARGIN)
}

function getDebtorSms(clientName, policyNumber, dayCount) {
  const first = (clientName || 'there').split(' ')[0]
  if (dayCount <= 3) {
    return `Hi ${first}, hope all is well. Just a friendly reminder that the insurance premium for policy ${policyNumber} is now 3 days overdue. Please pay via BPAY at your earliest convenience to ensure your cover remains in place and is not affected. If you would prefer to move onto monthly payments, please let us know and we can discuss available options. If you have already made payment, please disregard this message. Thanks, Benchmark Insurance Brokers`
  }
  return `Hi ${first}, hope all is well. We are just following up regarding the insurance premium for policy ${policyNumber}, which is now more than 7 days overdue. To avoid any issues with your insurance cover, please arrange payment via BPAY as soon as possible. If payment is not received, the insurer may take steps to cancel or suspend the policy. If you are experiencing any issues or would prefer to discuss monthly payment options, please let us know as soon as possible. If payment has already been made, please disregard this message. Thanks, Benchmark Insurance Brokers`
}

// ─── Shared visual helpers ────────────────────────────────────────────────────
const ESTIMATE_BORDER = '1.5px dashed rgba(30,61,48,0.35)'
function EstimateBadge() {
  return (
    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: '#7a5500', background: 'rgba(230,168,23,0.15)', border: '1px solid rgba(230,168,23,0.35)', borderRadius: 6, padding: '1px 6px', marginLeft: 6, verticalAlign: 'middle' }}>est.</span>
  )
}
function HeaderStat({ label, value, sub, color, estimate }) {
  return (
    <div style={{ minWidth: 150 }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        {label}
        {estimate && <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '.5px', color: '#ffd27a', border: '1px solid rgba(255,210,122,0.4)', borderRadius: 5, padding: '0 4px' }}>EST</span>}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, borderBottom: estimate ? '1.5px dashed rgba(255,255,255,0.25)' : 'none', paddingBottom: estimate ? 3 : 0, display: 'inline-block' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}
function StatCard({ label, value, color, sub, estimate }) {
  return (
    <div style={{ background: 'var(--surface)', border: estimate ? ESTIMATE_BORDER : '1.5px solid var(--border)', borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6, display: 'flex', alignItems: 'center' }}>
        {label}{estimate && <EstimateBadge />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

// ─── OVERVIEW TAB ─────────────────────────────────────────────────────────────
function OverviewTab({ coverConfirmations }) {
  const [mcRemittances, setMcRemittances] = useState([])
  const [fundingRemittances, setFundingRemittances] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const [{ data: mc }, { data: fr }] = await Promise.all([
        supabase.from('remittances').select('payment_date,total_amount').order('payment_date'),
        supabase.from('funding_remittances').select('created_at,period,total_amount').order('created_at'),
      ])
      setMcRemittances(mc || [])
      setFundingRemittances(fr || [])
      setLoading(false)
    }
    load()
  }, [])

  const today = new Date()

  // Combined dated actuals (McLardy by payment_date, funding by created_at)
  const datedActuals = useMemo(() => {
    const a = []
    mcRemittances.forEach(r => a.push({ date: new Date(r.payment_date), amt: r.total_amount || 0 }))
    fundingRemittances.forEach(r => a.push({ date: new Date(r.created_at), amt: r.total_amount || 0 }))
    return a.filter(x => !isNaN(x.date))
  }, [mcRemittances, fundingRemittances])

  const inYear = (d) => d >= YEAR_START && d <= today
  const ytdMc = useMemo(() => mcRemittances.filter(r => inYear(new Date(r.payment_date))).reduce((s, r) => s + (r.total_amount || 0), 0), [mcRemittances])
  const ytdFunding = useMemo(() => fundingRemittances.filter(r => inYear(new Date(r.created_at))).reduce((s, r) => s + (r.total_amount || 0), 0), [fundingRemittances])
  const ytdTotal = ytdMc + ytdFunding

  // Pace: steady (YTD-average) vs momentum (last 8 weeks)
  const msPerWeek = 7 * 24 * 60 * 60 * 1000
  const weeksElapsed = Math.max(1, (today - YEAR_START) / msPerWeek)
  const weeksTotal = (YEAR_END - YEAR_START) / msPerWeek
  const weeksRemaining = Math.max(0, weeksTotal - weeksElapsed)
  const weeklyTarget = 10000

  const steadyWeekly = ytdTotal / weeksElapsed
  const recentCutoff = new Date(today.getTime() - 8 * msPerWeek)
  const recentTotal = datedActuals.filter(x => x.date >= recentCutoff && x.date <= today).reduce((s, x) => s + x.amt, 0)
  const momentumWeekly = recentTotal / 8

  const steadyProjection = ytdTotal + steadyWeekly * weeksRemaining
  const momentumProjection = ytdTotal + momentumWeekly * weeksRemaining

  const paceStatus = momentumWeekly >= weeklyTarget ? 'ahead' : momentumWeekly >= weeklyTarget * 0.8 ? 'close' : 'behind'
  const paceColor = paceStatus === 'ahead' ? 'var(--forest)' : paceStatus === 'close' ? '#7a5500' : 'var(--danger)'

  const ringPct = Math.min(1, ytdTotal / ANNUAL_TARGET)
  const circumference = 2 * Math.PI * 54
  const strokeDash = circumference * ringPct

  // Rolling 12-month chart
  const chartMonths = useMemo(() => {
    const months = []
    for (let i = 0; i < 12; i++) {
      const d = new Date(YEAR_START)
      d.setMonth(d.getMonth() + i)
      const label = d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1)
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59)
      const isFuture = monthStart > today

      const mcActual = mcRemittances.filter(r => { const rd = new Date(r.payment_date); return rd >= monthStart && rd <= monthEnd }).reduce((s, r) => s + (r.total_amount || 0), 0)
      const fundActual = fundingRemittances.filter(r => { const rd = new Date(r.created_at); return rd >= monthStart && rd <= monthEnd }).reduce((s, r) => s + (r.total_amount || 0), 0)
      const forecastMc = isFuture ? (coverConfirmations || []).filter(cc => {
        const d2 = cc.cover_data?.period_from ? new Date(cc.cover_data.period_from) : null
        return d2 && d2 >= monthStart && d2 <= monthEnd && !cc.cover_data?._remittance_paid
      }).reduce((s, cc) => s + calcNetRev(cc.cover_data || {}), 0) : 0

      months.push({ label, mcActual, fundActual, forecastMc, isFuture })
    }
    return months
  }, [mcRemittances, fundingRemittances, coverConfirmations])

  const maxBarValue = Math.max(...chartMonths.map(m => m.mcActual + m.fundActual + m.forecastMc), weeklyTarget * 4.3)

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading...</span></div>

  return (
    <div>
      {/* Hero: target ring + projection */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 16, padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 28 }}>
          <svg width={128} height={128} viewBox="0 0 128 128">
            <circle cx={64} cy={64} r={54} fill="none" stroke="var(--border)" strokeWidth={10} />
            <circle cx={64} cy={64} r={54} fill="none" stroke="var(--forest)" strokeWidth={10} strokeDasharray={`${strokeDash} ${circumference}`} strokeLinecap="round" transform="rotate(-90 64 64)" style={{ transition: 'stroke-dasharray .6s ease' }} />
            <text x={64} y={58} textAnchor="middle" fill="var(--text)" fontSize={18} fontWeight={700}>{Math.round(ringPct * 100)}%</text>
            <text x={64} y={76} textAnchor="middle" fill="var(--muted)" fontSize={10}>of target</text>
          </svg>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Banked This Year</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--forest)', lineHeight: 1 }}>{fmtCurr(ytdTotal)}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>Target {fmtCurr(ANNUAL_TARGET)} · {fmtCurr(Math.max(0, ANNUAL_TARGET - ytdTotal))} to go</div>
            <div style={{ marginTop: 10, display: 'flex', gap: 16 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Commission</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurr(ytdMc)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Funding</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2563eb' }}>{fmtCurr(ytdFunding)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Projection to 6 Apr */}
        <div style={{ background: 'var(--surface)', border: `1.5px solid ${paceStatus === 'ahead' ? 'var(--forest-border)' : paceStatus === 'close' ? 'rgba(230,168,23,0.3)' : 'rgba(220,53,69,0.25)'}`, borderRadius: 16, padding: '24px 28px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>On Track For — by 6 Apr 2027</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: paceColor, lineHeight: 1 }}>{fmtCurr(momentumProjection)}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>at your last-8-week pace ({fmtCurr(momentumWeekly)}/wk)</div>
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Steady-pace landing</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>{fmtCurr(steadyProjection)}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{fmtCurr(steadyWeekly)}/wk avg all year</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Weeks left</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>{Math.floor(weeksRemaining)}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{Math.floor(weeksElapsed)} elapsed</div>
            </div>
          </div>
          <div style={{ marginTop: 14, height: 6, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 99, background: paceColor, width: `${Math.min(100, (momentumWeekly / weeklyTarget) * 100)}%`, transition: 'width .5s' }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
            {paceStatus === 'ahead' ? `${fmtCurr(momentumWeekly - weeklyTarget)}/wk ahead of the ${fmtCurr(weeklyTarget)} pace` : `${fmtCurr(weeklyTarget - momentumWeekly)}/wk behind the ${fmtCurr(weeklyTarget)} pace`}
          </div>
        </div>
      </div>

      {/* Rolling 12-month chart */}
      <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 16, padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Revenue by Month</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Apr 2026 – Apr 2027 · solid = banked · dashed = CRM forecast</div>
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--forest)', display: 'inline-block' }} /> Commission</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#2563eb', display: 'inline-block' }} /> Funding</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--border)', border: '1px dashed var(--muted)', display: 'inline-block' }} /> Forecast</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180 }}>
          {chartMonths.map((m, i) => {
            const total = m.mcActual + m.fundActual + m.forecastMc
            const barH = Math.round((total / maxBarValue) * 160)
            const mcH = total > 0 ? Math.round((m.mcActual / total) * barH) : 0
            const fundH = total > 0 ? Math.round((m.fundActual / total) * barH) : 0
            const foreH = barH - mcH - fundH
            const isCurrentMonth = !m.isFuture && i === chartMonths.findIndex(cm => cm.isFuture) - 1
            return (
              <div key={m.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {total > 0 && <div style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 600 }}>{fmtCurr(total)}</div>}
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: 160 }}>
                  <div style={{ width: '100%', borderRadius: '4px 4px 0 0', overflow: 'hidden', display: 'flex', flexDirection: 'column-reverse' }}>
                    {foreH > 0 && <div style={{ height: foreH, background: 'rgba(30,61,48,0.15)', borderTop: '1.5px dashed rgba(30,61,48,0.3)' }} />}
                    {fundH > 0 && <div style={{ height: fundH, background: '#2563eb' }} />}
                    {mcH > 0 && <div style={{ height: mcH, background: 'var(--forest)' }} />}
                    {total === 0 && <div style={{ height: 3, background: 'var(--border)', borderRadius: 2 }} />}
                  </div>
                </div>
                <div style={{ fontSize: 9, color: isCurrentMonth ? 'var(--forest)' : 'var(--muted)', fontWeight: isCurrentMonth ? 700 : 400, textAlign: 'center' }}>{m.label}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── CHANGE PAYMENT TYPE MODAL ────────────────────────────────────────────────
function ChangePaymentModal({ policy, onClose, onSave }) {
  const [funder, setFunder] = useState('Attvest')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    const updated = { ...policy.coverData, payment_type: 'monthly', premium_funder: funder, _paid_manually: false }
    await supabase.from('cover_confirmations').update({ cover_data: updated }).eq('id', policy.id)
    onSave()
    setSaving(false)
    onClose()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1.5px solid var(--border)', background: 'var(--surface2)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Change to Monthly Funding</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{policy.clientName} · {policy.policyNumber}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted)' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>Select Premium Funder</div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            {['Attvest', 'Victory'].map(f => (
              <button key={f} onClick={() => setFunder(f)} style={{ flex: 1, padding: '14px 0', borderRadius: 10, border: `2px solid ${funder === f ? 'var(--forest)' : 'var(--border)'}`, background: funder === f ? 'var(--forest)' : 'var(--surface2)', color: funder === f ? '#fff' : 'var(--muted)', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .15s' }}>
                {f}
                <div style={{ fontSize: 10, fontWeight: 500, marginTop: 4, opacity: 0.8 }}>Clears in 7 business days</div>
              </button>
            ))}
          </div>
          <div style={{ background: 'rgba(30,61,48,0.05)', border: '1.5px solid var(--forest-border)', borderRadius: 10, padding: '12px 14px', marginBottom: 20, fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>
            This moves <strong>{policy.clientName}</strong> from BPAY to <strong>{funder} monthly funding</strong>. It'll appear in the commission forecast once cleared.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" style={{ flex: 2 }} onClick={save} disabled={saving}>{saving ? 'Saving...' : `Switch to ${funder} →`}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── FORECAST TAB ─────────────────────────────────────────────────────────────
function ForecastTab({ coverConfirmations, clients, showToast }) {
  const paymentDates = useMemo(() => getPaymentDates(), [])
  const [changePaymentPolicy, setChangePaymentPolicy] = useState(null)
  const [fundingActuals, setFundingActuals] = useState([])

  useEffect(() => {
    supabase.from('funding_remittances').select('period,total_amount').then(({ data }) => setFundingActuals(data || []))
  }, [])

  const policies = useMemo(() => {
    return (coverConfirmations || []).map(cc => {
      const d = cc.cover_data || {}
      const cl = (clients || []).find(c => c.id === cc.client_id) || {}
      const basePrem = parseCurr(d.base_premium)
      const fee = parseCurr(d.broker_fee)
      const hasData = basePrem > 0 || fee > 0
      const netRev = calcNetRev(d)
      const method = getPaymentMethod(d)
      const startDate = d.period_from ? new Date(d.period_from) : null
      const alreadyPaid = d._remittance_paid === true
      const paidManually = d._paid_manually === true

      let clearanceDate = null
      let cycleIndex = null
      const clearedOverride = d._cleared_override ? new Date(d._cleared_override) : null
      if (hasData && !alreadyPaid) {
        if (method === 'attvest') {
          clearanceDate = clearedOverride || addBusinessDays(startDate, ATTVEST_DAYS)
          cycleIndex = assignToCycle(clearanceDate, paymentDates)
        } else if (method === 'victory') {
          clearanceDate = clearedOverride || addBusinessDays(startDate, VICTORY_DAYS)
          cycleIndex = assignToCycle(clearanceDate, paymentDates)
        } else if (method === 'bpay' && paidManually) {
          clearanceDate = clearedOverride || new Date()
          cycleIndex = assignToCycle(clearanceDate, paymentDates)
        }
      }

      return {
        id: cc.id,
        clientName: cl.company_name || d.insured_name || 'Unknown',
        coverType: d.cover_type || '—',
        policyNumber: d.policy_number || '—',
        insurer: d.insurer || '—',
        startDate, clearanceDate, cycleIndex, method, netRev, hasData,
        coverData: d, alreadyPaid, paidManually,
        isCancelled: !!d._cancelled, clearedOverride: !!clearedOverride,
      }
    }).filter(p => p.hasData && !p.alreadyPaid && !p.isCancelled && (p.method !== 'bpay' || p.paidManually))
  }, [coverConfirmations, clients, paymentDates])

  const cycles = useMemo(() => paymentDates.map((pd, i) => ({
    date: pd,
    cutoff: getCutoff(pd),
    policies: policies.filter(p => p.cycleIndex === i),
    total: policies.filter(p => p.cycleIndex === i).reduce((s, p) => s + p.netRev, 0),
  })), [policies, paymentDates])

  // Funding: expected (clearance month) vs received (statement period)
  const monthlyFunding = useMemo(() => {
    const monthKey = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`
    const monthLabel = (y, m) => new Date(y, m, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
    const map = {}
    const ensure = (y, m) => {
      const k = monthKey(y, m)
      if (!map[k]) map[k] = { key: k, label: monthLabel(y, m), sortY: y, sortM: m, expected: 0, expectedCount: 0, actual: 0 }
      return map[k]
    }
    ;(coverConfirmations || []).forEach(cc => {
      const d = cc.cover_data || {}
      if (d._cancelled) return
      const method = getPaymentMethod(d)
      if (method !== 'attvest' && method !== 'victory') return
      if (parseCurr(d.base_premium) <= 0) return
      const start = d.period_from ? new Date(d.period_from) : null
      if (!start || isNaN(start)) return
      const clearance = addBusinessDays(start, method === 'attvest' ? ATTVEST_DAYS : VICTORY_DAYS)
      const row = ensure(clearance.getFullYear(), clearance.getMonth())
      row.expected += calcFundingIncome(d)
      row.expectedCount += 1
    })
    ;(fundingActuals || []).forEach(r => {
      const p = parseFundingPeriod(r.period)
      if (!p) return
      const row = ensure(p.year, p.month)
      row.actual += r.total_amount || 0
    })
    return Object.values(map).sort((a, b) => a.sortY - b.sortY || a.sortM - b.sortM)
  }, [coverConfirmations, fundingActuals])

  const fundingExpectedTotal = monthlyFunding.reduce((s, m) => s + m.expected, 0)
  const fundingActualTotal = monthlyFunding.reduce((s, m) => s + m.actual, 0)

  const bpayPending = useMemo(() => {
    return (coverConfirmations || []).map(cc => {
      const d = cc.cover_data || {}
      const cl = (clients || []).find(c => c.id === cc.client_id) || {}
      const method = getPaymentMethod(d)
      if (method !== 'bpay') return null
      if (d._remittance_paid || d._paid_manually || d._cancelled) return null
      const hasData = parseCurr(d.base_premium) > 0 || parseCurr(d.broker_fee) > 0
      if (!hasData) return null
      return {
        id: cc.id,
        clientName: cl.company_name || d.insured_name || 'Unknown',
        coverType: d.cover_type || '—',
        policyNumber: d.policy_number || '—',
        insurer: d.insurer || '—',
        startDate: d.period_from ? new Date(d.period_from) : null,
        netRev: calcNetRev(d),
        coverData: d,
      }
    }).filter(Boolean)
  }, [coverConfirmations, clients])

  const markBpayPaid = async (policyId, row) => {
    await supabase.from('cover_confirmations').update({ cover_data: { ...row.coverData, _paid_manually: true } }).eq('id', policyId)
    showToast('Marked as paid')
    window.location.reload()
  }

  const markClearedNow = async (policyId, row) => {
    const isOn = !!row.coverData._cleared_override
    const updated = { ...row.coverData }
    if (isOn) delete updated._cleared_override
    else updated._cleared_override = new Date().toISOString()
    await supabase.from('cover_confirmations').update({ cover_data: updated }).eq('id', policyId)
    showToast(isOn ? 'Reverted to forecast clearance date' : 'Confirmed cleared — moved to current cycle')
    window.location.reload()
  }

  return (
    <div>
      {changePaymentPolicy && (
        <ChangePaymentModal policy={changePaymentPolicy} onClose={() => setChangePaymentPolicy(null)} onSave={() => { showToast('Payment type updated — reloading...'); setTimeout(() => window.location.reload(), 800) }} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, padding: '8px 14px', background: 'var(--surface2)', borderRadius: 10, fontSize: 11, color: 'var(--muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 24, height: 0, borderTop: ESTIMATE_BORDER, display: 'inline-block' }} /> Dashed = forecast</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 24, height: 0, borderTop: '1.5px solid var(--forest)', display: 'inline-block' }} /> Solid = banked</span>
        <span style={{ marginLeft: 'auto' }}>Actuals live in <strong>Remittance History</strong></span>
      </div>

      {/* McLardy cycle cards */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>McLardy Commission Forecast <EstimateBadge /></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
        {cycles.map((cycle, i) => (
          <div key={i} style={{ background: 'var(--surface)', border: ESTIMATE_BORDER, borderRadius: 14, overflow: 'hidden', boxShadow: i === 0 ? '0 4px 16px rgba(30,61,48,0.1)' : 'none' }}>
            <div style={{ background: i === 0 ? 'var(--forest)' : 'var(--surface2)', padding: '14px 18px', borderBottom: '1.5px solid var(--border)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: i === 0 ? 'rgba(255,255,255,0.6)' : 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 4 }}>
                {i === 0 ? 'Next Payment' : i === 1 ? 'Following Payment' : 'Payment After'}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: i === 0 ? '#fff' : 'var(--text)' }}>{cycle.date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              <div style={{ fontSize: 11, color: i === 0 ? 'rgba(255,255,255,0.5)' : 'var(--muted)', marginTop: 3 }}>Cutoff: {fmtDate(cycle.cutoff)}</div>
            </div>
            <div style={{ padding: '14px 18px' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: i === 0 ? 'var(--forest)' : 'var(--text)', marginBottom: 4 }}>{fmtCurrDec(cycle.total)}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{cycle.policies.length} {cycle.policies.length === 1 ? 'policy' : 'policies'} clearing</div>
              {cycle.policies.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {cycle.policies.slice(0, 4).map(p => (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
                      <span style={{ color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{p.clientName}</span>
                      <span style={{ color: 'var(--forest)', fontWeight: 700, flexShrink: 0 }}>{fmtCurr(p.netRev)}</span>
                    </div>
                  ))}
                  {cycle.policies.length > 4 && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>+{cycle.policies.length - 4} more</div>}
                </div>
              )}
              {cycle.policies.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic' }}>Nothing forecast yet</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Funding: estimate vs received */}
      {monthlyFunding.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, marginTop: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px' }}>Funding Income — Estimate vs Received <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>· Attvest &amp; Victory</span></div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>To date: <strong style={{ color: '#7a5500' }}>{fmtCurrDec(fundingExpectedTotal)}</strong> est <EstimateBadge /> · <strong style={{ color: 'var(--forest)' }}>{fmtCurrDec(fundingActualTotal)}</strong> received</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--text)' }}>Estimate</strong> is a forecast — each policy's premium × its funder rate. <strong style={{ color: 'var(--text)' }}>Received</strong> is what actually landed on the statement. Small monthly gaps are normal: funders pay across month boundaries and each policy's exact rate is set when it's funded. <strong>Received is the real money.</strong>
          </div>
          <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1.2fr', gap: 10, padding: '10px 20px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
              {['Month', 'Estimate', 'Received', 'vs estimate'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', textAlign: h === 'Month' ? 'left' : 'right' }}>{h}</div>
              ))}
            </div>
            {monthlyFunding.map(m => {
              const hasActual = m.actual > 0
              const diff = m.actual - m.expected
              const pctOff = m.expected > 0 ? Math.abs(diff) / m.expected : 0
              let statusText = 'awaiting statement', statusColor = 'var(--muted)'
              if (hasActual) {
                if (pctOff < 0.1) { statusText = '✓ on track'; statusColor = 'var(--forest)' }
                else if (diff < 0) { statusText = `${fmtCurrDec(diff)} · timing/rate`; statusColor = '#7a5500' }
                else { statusText = `+${fmtCurrDec(diff)}`; statusColor = 'var(--forest)' }
              }
              return (
                <div key={m.key} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1.2fr', gap: 10, padding: '12px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{m.label}<span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400, marginLeft: 6 }}>{m.expectedCount} {m.expectedCount === 1 ? 'policy' : 'policies'}</span></div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#7a5500', textAlign: 'right', borderBottom: m.expected > 0 ? ESTIMATE_BORDER : 'none', paddingBottom: 2 }}>{m.expected > 0 ? fmtCurrDec(m.expected) : '—'}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: hasActual ? 'var(--forest)' : 'var(--muted)', textAlign: 'right' }}>{hasActual ? fmtCurrDec(m.actual) : '—'}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'right', color: statusColor }}>{statusText}</div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* McLardy policy detail tables */}
      {cycles.map((cycle, ci) => cycle.policies.length > 0 && (
        <div key={ci} style={{ marginBottom: 20, background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: ci === 0 ? 'rgba(30,61,48,0.04)' : 'var(--surface2)' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Payment {cycle.date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' })} — {cycle.policies.length} {cycle.policies.length === 1 ? 'policy' : 'policies'}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Clears on or before {fmtDate(cycle.cutoff)}</div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurrDec(cycle.total)}</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 110px', gap: 10, padding: '8px 20px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
              {['Client', 'Cover', 'Method', 'Start Date', 'Clears', 'Your Revenue', ''].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>
              ))}
            </div>
            {cycle.policies.map(p => (
              <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 110px', gap: 10, padding: '12px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.clientName}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.policyNumber}</div>
                </div>
                <div style={{ fontSize: 12 }}>{p.coverType}</div>
                <div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: p.method === 'attvest' ? 'rgba(59,130,246,0.1)' : p.method === 'victory' ? 'rgba(139,92,246,0.1)' : 'rgba(30,61,48,0.1)', color: p.method === 'attvest' ? '#2563eb' : p.method === 'victory' ? '#7c3aed' : 'var(--forest)' }}>
                    {p.method === 'attvest' ? 'Attvest' : p.method === 'victory' ? 'Victory' : 'BPAY Paid'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{fmtDate(p.startDate)}</div>
                <div style={{ fontSize: 12, color: 'var(--forest)', fontWeight: 600 }}>
                  {fmtDate(p.clearanceDate)}
                  {p.clearedOverride && <div style={{ fontSize: 9, color: '#2563eb', fontWeight: 700 }}>✓ confirmed</div>}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurrDec(p.netRev)}</div>
                <div>
                  {(p.method === 'attvest' || p.method === 'victory') && (
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 9, padding: '4px 8px', color: p.clearedOverride ? 'var(--muted)' : '#2563eb', borderColor: p.clearedOverride ? 'var(--border)' : 'rgba(59,130,246,0.3)' }} onClick={() => markClearedNow(p.id, p)}>
                      {p.clearedOverride ? '↩ Undo' : '✓ Cleared now'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* BPAY pending */}
      {bpayPending.length > 0 && (
        <div style={{ background: 'var(--surface)', border: ESTIMATE_BORDER, borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1.5px solid rgba(230,168,23,0.2)', background: 'rgba(230,168,23,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#7a5500' }}>Awaiting Client Payment (Annual BPAY) <EstimateBadge /></div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Commission releases once client pays — check then mark as paid to move into forecast</div>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#7a5500' }}>{fmtCurrDec(bpayPending.reduce((s, p) => s + p.netRev, 0))} pending</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px', gap: 10, padding: '8px 20px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
            {['Client', 'Cover', 'Insurer', 'Start Date', 'Your Revenue', ''].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>
            ))}
          </div>
          {bpayPending.map(p => (
            <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px', gap: 10, padding: '12px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.clientName}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.policyNumber}</div>
              </div>
              <div style={{ fontSize: 12 }}>{p.coverType}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{p.insurer}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{fmtDate(p.startDate)}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#7a5500' }}>{fmtCurrDec(p.netRev)}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 8px', color: 'var(--forest)', borderColor: 'var(--forest-border)' }} onClick={() => markBpayPaid(p.id, p)}>✓ Paid</button>
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 8px', color: '#2563eb', borderColor: 'rgba(59,130,246,0.3)' }} onClick={() => setChangePaymentPolicy({ id: p.id, clientName: p.clientName, policyNumber: p.policyNumber, coverData: p.coverData })}>📅 Monthly</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── DEBTOR TAB ───────────────────────────────────────────────────────────────
function DebtorTab({ coverConfirmations, clients, showToast, user, onRefresh }) {
  const [sendingSms, setSendingSms] = useState({})
  const [smsSent, setSmsSent] = useState({})
  const [paidIds, setPaidIds] = useState([])
  const [changePaymentPolicy, setChangePaymentPolicy] = useState(null)   // hooks always run — declared before any early return

  const debtors = useMemo(() => {
    return (coverConfirmations || [])
      .map(cc => {
        const d = cc.cover_data || {}
        const cl = (clients || []).find(c => c.id === cc.client_id) || {}
        const method = getPaymentMethod(d)
        if (method !== 'bpay') return null
        if (d._remittance_paid || d._paid_manually) return null
        const startDate = d.period_from ? new Date(d.period_from) : null
        if (!startDate) return null
        const daysOld = daysSince(startDate)
        const hasData = parseCurr(d.base_premium) > 0 || parseCurr(d.broker_fee) > 0
        return {
          id: cc.id,
          clientName: cl.company_name || d.insured_name || 'Unknown',
          clientPhone: cl.phone || '',
          coverType: d.cover_type || '—',
          policyNumber: d.policy_number || '—',
          insurer: d.insurer || '—',
          broker: cc.created_by || '—',
          premium: parseCurr(d.total_invoice),
          startDate, daysOld, netRev: calcNetRev(d), hasData,
          coverData: d, clientId: cc.client_id,
          smsDay3Sent: d._sms_day3_sent || false,
          smsDay7Sent: d._sms_day7_sent || false,
        }
      })
      .filter(Boolean)
      .filter(d => d.daysOld >= 0)
      .filter(d => !paidIds.includes(d.id))
      .sort((a, b) => b.daysOld - a.daysOld)
  }, [coverConfirmations, clients, paidIds])

  const sendDebtorSms = async (debtor, dayTier, preEditedBody) => {
    if (!debtor.clientPhone) return showToast('No phone number on file')
    const smsKey = `${debtor.id}_${dayTier}`
    setSendingSms(prev => ({ ...prev, [smsKey]: true }))
    try {
      const body = preEditedBody || getDebtorSms(debtor.clientName, debtor.policyNumber, dayTier)
      const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: debtor.clientPhone, body, client_id: debtor.clientId, client_name: debtor.clientName, broker_name: user?.name || 'Benchmark' }),
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error || 'Send failed')
      const flagKey = dayTier <= 3 ? '_sms_day3_sent' : '_sms_day7_sent'
      await supabase.from('cover_confirmations').update({ cover_data: { ...debtor.coverData, [flagKey]: true } }).eq('id', debtor.id)
      setSmsSent(prev => ({ ...prev, [smsKey]: true }))
      showToast(`Day ${dayTier} SMS sent to ${debtor.clientName}`)
    } catch(e) { showToast(`SMS failed: ${e.message}`) }
    setSendingSms(prev => ({ ...prev, [smsKey]: false }))
  }

  const markPaid = async (debtor) => {
    await supabase.from('cover_confirmations').update({ cover_data: { ...debtor.coverData, _paid_manually: true } }).eq('id', debtor.id)
    showToast('Marked as paid — moving to commission forecast')
    if (onRefresh) await onRefresh()
    setPaidIds(prev => [...prev, debtor.id])
  }

  const urgentDebtors = debtors.filter(d => d.daysOld >= 7)
  const followUpDebtors = debtors.filter(d => d.daysOld >= 3 && d.daysOld < 7)
  const newDebtors = debtors.filter(d => d.daysOld < 3)
  const totalOutstanding = debtors.reduce((s, d) => s + (d.premium || 0), 0)
  const totalRevAtRisk = debtors.filter(d => d.hasData).reduce((s, d) => s + d.netRev, 0)

  const onChangePayment = d => setChangePaymentPolicy({ id: d.id, clientName: d.clientName, policyNumber: d.policyNumber, coverData: d.coverData })

  if (debtors.length === 0) {
    return (
      <div>
        {changePaymentPolicy && <ChangePaymentModal policy={changePaymentPolicy} onClose={() => setChangePaymentPolicy(null)} onSave={() => { showToast('Payment type updated — reloading...'); setTimeout(() => window.location.reload(), 800) }} />}
        <div className="empty" style={{ padding: '48px 24px' }}>
          <div className="empty-icon">🎉</div>
          <div className="empty-title">No outstanding BPAY debtors</div>
          <div className="empty-sub">All annual clients have paid or been reconciled via remittance</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {changePaymentPolicy && <ChangePaymentModal policy={changePaymentPolicy} onClose={() => setChangePaymentPolicy(null)} onSave={() => { showToast('Payment type updated — reloading...'); setTimeout(() => window.location.reload(), 800) }} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label="Outstanding Debtors" value={debtors.length} color="var(--danger)" />
        <StatCard label="Total Premium Unpaid" value={fmtCurr(totalOutstanding)} />
        <StatCard label="Revenue At Risk" value={fmtCurr(totalRevAtRisk)} color="#7a5500" />
        <StatCard label="Urgent (7+ days)" value={urgentDebtors.length} color={urgentDebtors.length > 0 ? 'var(--danger)' : 'var(--forest)'} />
      </div>
      {urgentDebtors.length > 0 && <DebtorSection title="🚨 Urgent — 7+ Days" debtors={urgentDebtors} accentColor="var(--danger)" bgColor="rgba(220,53,69,0.04)" borderColor="rgba(220,53,69,0.25)" sendingSms={sendingSms} smsSent={smsSent} onSendSms={sendDebtorSms} onMarkPaid={markPaid} onChangePayment={onChangePayment} />}
      {followUpDebtors.length > 0 && <DebtorSection title="⚠️ Follow-Up — 3–6 Days" debtors={followUpDebtors} accentColor="#7a5500" bgColor="rgba(230,168,23,0.04)" borderColor="rgba(230,168,23,0.25)" sendingSms={sendingSms} smsSent={smsSent} onSendSms={sendDebtorSms} onMarkPaid={markPaid} onChangePayment={onChangePayment} />}
      {newDebtors.length > 0 && <DebtorSection title="🕐 New — Under 3 Days" debtors={newDebtors} accentColor="var(--forest)" bgColor="rgba(30,61,48,0.03)" borderColor="var(--forest-border)" sendingSms={sendingSms} smsSent={smsSent} onSendSms={sendDebtorSms} onMarkPaid={markPaid} onChangePayment={onChangePayment} />}
    </div>
  )
}

function DebtorSection({ title, debtors, accentColor, bgColor, borderColor, sendingSms, smsSent, onSendSms, onMarkPaid, onChangePayment }) {
  const [smsModal, setSmsModal] = useState(null)
  const openSmsModal = (debtor, dayTier) => setSmsModal({ debtor, dayTier, body: getDebtorSms(debtor.clientName, debtor.policyNumber, dayTier) })
  const confirmSend = async () => { if (!smsModal) return; await onSendSms(smsModal.debtor, smsModal.dayTier, smsModal.body); setSmsModal(null) }

  return (
    <>
      {smsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1.5px solid var(--border)', background: 'var(--surface2)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Review SMS before sending</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>To: <strong>{smsModal.debtor.clientName}</strong> · {smsModal.debtor.clientPhone}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: smsModal.dayTier <= 3 ? 'rgba(230,168,23,0.12)' : 'rgba(220,53,69,0.1)', color: smsModal.dayTier <= 3 ? '#7a5500' : 'var(--danger)' }}>Day {smsModal.dayTier} SMS</span>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Message — edit before sending if needed</div>
              <textarea value={smsModal.body} onChange={e => setSmsModal(prev => ({ ...prev, body: e.target.value }))} rows={7} style={{ width: '100%', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, padding: '12px 14px', fontFamily: 'var(--font)', lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>{smsModal.body.length} characters</div>
            </div>
            <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => setSmsModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={confirmSend}>Send SMS to {smsModal.debtor.clientName.split(' ')[0]}</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ background: 'var(--surface)', border: `1.5px solid ${borderColor}`, borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '14px 20px', borderBottom: `1.5px solid ${borderColor}`, background: bgColor, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: accentColor }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{debtors.length} {debtors.length === 1 ? 'client' : 'clients'}</div>
        </div>
        {debtors.map(debtor => {
          const sent3 = debtor.smsDay3Sent || smsSent[`${debtor.id}_3`]
          const sent7 = debtor.smsDay7Sent || smsSent[`${debtor.id}_7`]
          const sending3 = sendingSms[`${debtor.id}_3`]
          const sending7 = sendingSms[`${debtor.id}_7`]
          return (
            <div key={debtor.id} style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '2fr 1fr 0.8fr 0.8fr 0.8fr auto', gap: 12, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{debtor.clientName}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{debtor.coverType} · {debtor.policyNumber}</div>
                {debtor.clientPhone && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{debtor.clientPhone}</div>}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{fmtCurr(debtor.premium)} premium</div>
                {debtor.hasData && <div style={{ fontSize: 11, color: 'var(--forest)' }}>{fmtCurr(debtor.netRev)} your rev</div>}
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: debtor.daysOld >= 7 ? 'rgba(220,53,69,0.1)' : debtor.daysOld >= 3 ? 'rgba(230,168,23,0.12)' : 'rgba(30,61,48,0.08)', color: debtor.daysOld >= 7 ? 'var(--danger)' : debtor.daysOld >= 3 ? '#7a5500' : 'var(--forest)' }}>
                  {debtor.daysOld === 0 ? 'Today' : `${debtor.daysOld}d`}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtDateShort(debtor.startDate)}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{debtor.broker?.split(' ')[0]}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {debtor.daysOld >= 3 && (
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 10px', color: sent3 ? 'var(--forest)' : '#7a5500', borderColor: sent3 ? 'var(--forest-border)' : 'rgba(230,168,23,0.4)', opacity: sending3 ? 0.6 : 1 }} disabled={sending3 || !debtor.clientPhone} onClick={() => openSmsModal(debtor, 3)}>
                    {sending3 ? '...' : sent3 ? '✓ 3d sent' : '💬 Day 3 SMS'}
                  </button>
                )}
                {debtor.daysOld >= 7 && (
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 10px', color: sent7 ? 'var(--forest)' : 'var(--danger)', borderColor: sent7 ? 'var(--forest-border)' : 'rgba(220,53,69,0.3)', opacity: sending7 ? 0.6 : 1 }} disabled={sending7 || !debtor.clientPhone} onClick={() => openSmsModal(debtor, 7)}>
                    {sending7 ? '...' : sent7 ? '✓ 7d sent' : '🚨 Day 7 SMS'}
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 10px', color: 'var(--forest)', borderColor: 'var(--forest-border)' }} onClick={() => onMarkPaid(debtor)}>✓ Paid</button>
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 10px', color: '#2563eb', borderColor: 'rgba(59,130,246,0.3)' }} onClick={() => onChangePayment && onChangePayment(debtor)}>📅 Monthly</button>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

// ─── EXPENSES TAB ─────────────────────────────────────────────────────────────
// Requires an `expenses` table (see migration in the chat). Degrades gracefully
// with a setup prompt if the table isn't there yet.
const EXPENSE_CATEGORIES = ['Software', 'Wages', 'Rent', 'Marketing', 'Insurance', 'Subscriptions', 'Travel', 'Office', 'Fees', 'Other']

function ExpensesTab({ user, showToast }) {
  const [expenses, setExpenses] = useState([])
  const [received, setReceived] = useState(0)
  const [loading, setLoading] = useState(true)
  const [tableMissing, setTableMissing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], category: 'Software', description: '', amount: '' })

  const load = useCallback(async () => {
    setLoading(true)
    const [{ data: exp, error }, { data: mc }, { data: fund }] = await Promise.all([
      supabase.from('expenses').select('*').order('date', { ascending: false }),
      supabase.from('remittances').select('payment_date,total_amount'),
      supabase.from('funding_remittances').select('created_at,total_amount'),
    ])
    if (error && (error.code === '42P01' || /relation .*expenses.* does not exist/i.test(error.message || ''))) {
      setTableMissing(true); setLoading(false); return
    }
    setExpenses(exp || [])
    const inYear = (d) => { const x = new Date(d); return x >= YEAR_START && x <= new Date() }
    const mcYtd = (mc || []).filter(r => inYear(r.payment_date)).reduce((s, r) => s + (r.total_amount || 0), 0)
    const fundYtd = (fund || []).filter(r => inYear(r.created_at)).reduce((s, r) => s + (r.total_amount || 0), 0)
    setReceived(mcYtd + fundYtd)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const addExpense = async () => {
    const amt = parseCurr(form.amount)
    if (!amt || !form.date) return showToast('Enter a date and amount')
    setSaving(true)
    const { error } = await supabase.from('expenses').insert({
      date: form.date, category: form.category, description: form.description || null, amount: amt, created_by: user?.name || null,
    })
    setSaving(false)
    if (error) return showToast('Save error: ' + error.message)
    setForm({ date: new Date().toISOString().split('T')[0], category: 'Software', description: '', amount: '' })
    showToast('Expense added')
    load()
  }

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return
    await supabase.from('expenses').delete().eq('id', id)
    showToast('Deleted')
    load()
  }

  const now = new Date()
  const inThisMonth = (d) => { const x = new Date(d); return x.getFullYear() === now.getFullYear() && x.getMonth() === now.getMonth() }
  const inThisYear = (d) => { const x = new Date(d); return x >= YEAR_START && x <= now }
  const monthTotal = expenses.filter(e => inThisMonth(e.date)).reduce((s, e) => s + (e.amount || 0), 0)
  const yearTotal = expenses.filter(e => inThisYear(e.date)).reduce((s, e) => s + (e.amount || 0), 0)
  const netProfit = received - yearTotal

  const byCategory = useMemo(() => {
    const map = {}
    expenses.filter(e => inThisYear(e.date)).forEach(e => { map[e.category || 'Other'] = (map[e.category || 'Other'] || 0) + (e.amount || 0) })
    return Object.entries(map).sort((a, b) => b[1] - a[1])
  }, [expenses])

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading...</span></div>

  if (tableMissing) {
    return (
      <div className="empty" style={{ padding: '40px 24px', textAlign: 'center' }}>
        <div className="empty-icon">🧾</div>
        <div className="empty-title">Expenses table not set up yet</div>
        <div className="empty-sub" style={{ maxWidth: 460, margin: '8px auto 0' }}>Run the one-time <strong>expenses</strong> migration in Supabase (in the chat), then refresh. Once it's there, this tab tracks spend and shows net profit against banked revenue.</div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label="Spent This Month" value={fmtCurr(monthTotal)} color="var(--danger)" />
        <StatCard label="Spent This Year" value={fmtCurr(yearTotal)} color="var(--danger)" />
        <StatCard label="Banked This Year" value={fmtCurr(received)} color="var(--forest)" />
        <StatCard label="Net Profit (YTD)" value={fmtCurr(netProfit)} color={netProfit >= 0 ? 'var(--forest)' : 'var(--danger)'} sub="banked revenue − expenses" />
      </div>

      {/* Add expense */}
      <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>Add an expense</div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 160px 1fr 130px 90px', gap: 10, alignItems: 'center' }}>
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={{ padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)' }} />
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)' }}>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="text" placeholder="Description (optional)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)' }} />
          <input type="text" inputMode="decimal" placeholder="$0.00" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={{ padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', textAlign: 'right' }} />
          <button className="btn btn-primary btn-sm" onClick={addExpense} disabled={saving}>{saving ? '...' : 'Add'}</button>
        </div>
      </div>

      {/* Category breakdown */}
      {byCategory.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>This year by category</div>
          {byCategory.map(([cat, amt]) => {
            const pct = yearTotal > 0 ? (amt / yearTotal) * 100 : 0
            return (
              <div key={cat} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{cat}</span>
                  <span style={{ color: 'var(--muted)' }}>{fmtCurr(amt)} · {pct.toFixed(0)}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: 'var(--forest)', borderRadius: 99 }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Expense list */}
      {expenses.length === 0 ? (
        <div className="empty" style={{ padding: '28px 24px' }}><div className="empty-icon">🧾</div><div className="empty-title">No expenses logged yet</div><div className="empty-sub">Add your first one above</div></div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr 60px', gap: 10, padding: '8px 20px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
            {['Date', 'Category', 'Description', 'Amount', ''].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</div>)}
          </div>
          {expenses.map(e => (
            <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr 60px', gap: 10, padding: '12px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(e.date)}</div>
              <div><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(30,61,48,0.08)', color: 'var(--forest)' }}>{e.category || 'Other'}</span></div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{e.description || '—'}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--danger)', textAlign: 'right' }}>{fmtCurrDec(e.amount)}</div>
              <div style={{ textAlign: 'right' }}><button className="btn btn-danger btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => deleteExpense(e.id)}>✕</button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── FUNDING REMITTANCE SECTION ───────────────────────────────────────────────
function FundingRemittanceSection({ showToast }) {
  const [remittances, setRemittances] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [preview, setPreview] = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [itemsCache, setItemsCache] = useState({})

  useEffect(() => { load() }, [])

  const load = async () => {
    setLoading(true)
    const { data } = await supabase.from('funding_remittances').select('*').order('created_at', { ascending: false })
    setRemittances(data || [])
    setLoading(false)
  }

  const loadItems = async (id) => {
    if (expandedId === id) { setExpandedId(null); return }
    if (!itemsCache[id]) {
      const { data } = await supabase.from('funding_remittance_items').select('*').eq('remittance_id', id).order('date_paid')
      setItemsCache(prev => ({ ...prev, [id]: data || [] }))
    }
    setExpandedId(id)
  }

  const deleteRemittance = async (id) => {
    if (!window.confirm('Delete this funding statement?')) return
    await supabase.from('funding_remittances').delete().eq('id', id)
    await load()
    showToast('Deleted')
  }

  const parseExcel = async (file) => {
    setParsing(true)
    try {
      if (!window.XLSX) {
        const script = document.createElement('script')
        script.src = 'https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js'
        await new Promise((res, rej) => { script.onload = res; script.onerror = rej; document.head.appendChild(script) })
      }
      const XLSX = window.XLSX
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })

      const headerRowIdx = rows.findIndex(r => r && r.some(c => c && c.toString().includes('CLIENT')))
      if (headerRowIdx === -1) throw new Error('Could not find header row — is this an Attvest/Victory funding statement?')

      const headers = rows[headerRowIdx]
      const colIdx = (name) => headers.findIndex(h => h && h.toString().toUpperCase().includes(name.toUpperCase()))
      const dateCol = colIdx('DATE'), clientCol = colIdx('CLIENT'), funderCol = colIdx('FUNDER')
      const premiumCol = colIdx('PREMIUM'), commPctCol = colIdx('COMMISSION %'), commDolCol = colIdx('COMMISSION $')
      const mmpCol = colIdx('MMP'), amountPaidCol = colIdx('Amount Paid')

      const totalAmount = rows[0] ? Number(rows[0][rows[0].length - 1]) || 0 : 0
      const periodMatch = file.name.match(/([A-Z]{3}[-_]?\d{2})/i)
      const period = periodMatch ? periodMatch[1].replace('_', '-').toUpperCase() : new Date().toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }).toUpperCase()

      const rawItems = []
      for (let i = headerRowIdx + 1; i < rows.length; i++) {
        const r = rows[i]
        if (!r || !r[clientCol]) continue
        rawItems.push({
          date_paid: r[dateCol] ? new Date(r[dateCol]).toISOString().split('T')[0] : null,
          client_name: r[clientCol] || '', funder: r[funderCol] || '',
          premium_amount: Number(r[premiumCol]) || 0, commission_pct: Number(r[commPctCol]) || 0,
          commission_gross: Number(r[commDolCol]) || 0, mmp_margin: Number(r[mmpCol]) || 0,
          amount_paid: Number(r[amountPaidCol]) || 0,
        })
      }

      const netted = {}
      rawItems.forEach(item => {
        const key = `${item.client_name}||${item.funder}`
        if (!netted[key]) netted[key] = { ...item, amount_paid: 0, commission_gross: 0, premium_amount: 0 }
        netted[key].amount_paid += item.amount_paid
        netted[key].commission_gross += item.commission_gross
        netted[key].premium_amount += item.premium_amount
      })
      const items = Object.values(netted).filter(item => Math.abs(item.amount_paid) > 0.01)
      const funderSummary = {}
      items.forEach(item => {
        const f = item.funder || 'Unknown'
        if (!funderSummary[f]) funderSummary[f] = { count: 0, total: 0 }
        funderSummary[f].count++; funderSummary[f].total += item.amount_paid
      })
      setPreview({ period, totalAmount, items, fileName: file.name, funderSummary })
    } catch(e) { showToast('Parse error: ' + e.message) }
    setParsing(false)
  }

  const handleDrop = async (e) => {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (!file) return
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) return showToast('Please drop an Excel file (.xlsx)')
    await parseExcel(file)
  }
  const handleFileInput = async (e) => { const file = e.target.files[0]; if (!file) return; await parseExcel(file); e.target.value = '' }

  const savePreview = async () => {
    if (!preview) return
    setSaving(true)
    try {
      const funders = Object.keys(preview.funderSummary).join(' + ')
      const { data: remData, error: remErr } = await supabase.from('funding_remittances').insert({
        period: preview.period, funder: funders, total_amount: preview.totalAmount, file_name: preview.fileName,
        notes: Object.entries(preview.funderSummary).map(([f, s]) => `${f}: ${s.count} policies, ${fmtCurrDec(s.total)}`).join(' · '),
      }).select().single()
      if (remErr) throw new Error(remErr.message)
      if (preview.items.length > 0) {
        const { error: itemErr } = await supabase.from('funding_remittance_items').insert(
          preview.items.map(item => ({ remittance_id: remData.id, date_paid: item.date_paid, client_name: item.client_name, funder: item.funder, premium_amount: item.premium_amount, commission_pct: item.commission_pct, commission_gross: item.commission_gross, mmp_margin: item.mmp_margin, amount_paid: item.amount_paid }))
        )
        if (itemErr) throw new Error(itemErr.message)
      }
      await load(); setPreview(null)
      showToast(`✅ Funding statement imported — ${preview.items.length} policies, ${fmtCurrDec(preview.totalAmount)} total`)
    } catch(e) { showToast('Save error: ' + e.message) }
    setSaving(false)
  }

  const totalReceived = remittances.reduce((s, r) => s + (r.total_amount || 0), 0)
  const thisYearTotal = remittances.filter(r => r.created_at?.startsWith(new Date().getFullYear().toString())).reduce((s, r) => s + (r.total_amount || 0), 0)
  const thisMonthTotal = remittances.filter(r => isPeriodThisMonth(r.period)).reduce((s, r) => s + (r.total_amount || 0), 0)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>💳 Funding Income Statements</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Attvest & Victory monthly funding payments — separate to McLardy commission</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label="Received This Month" value={fmtCurrDec(thisMonthTotal)} color="#4ade80" />
        <StatCard label="Total Funding Received" value={fmtCurrDec(totalReceived)} color="#2563eb" />
        <StatCard label={`Received ${new Date().getFullYear()}`} value={fmtCurrDec(thisYearTotal)} color="#7c3aed" />
        <StatCard label="Statements Imported" value={remittances.length} color="var(--forest)" />
      </div>

      {!preview && (
        <div onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragOver(true) }} onDragLeave={() => setDragOver(false)} style={{ border: `2px dashed ${dragOver ? '#2563eb' : 'var(--border)'}`, borderRadius: 14, padding: '24px 20px', textAlign: 'center', marginBottom: 20, background: dragOver ? 'rgba(59,130,246,0.04)' : 'var(--surface)', cursor: 'pointer', transition: 'all .15s' }} onClick={() => document.getElementById('funding-file-input').click()}>
          <input id="funding-file-input" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileInput} />
          {parsing ? <div style={{ fontSize: 13, color: 'var(--muted)' }}>Parsing funding statement...</div> : <>
            <div style={{ fontSize: 24, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Drop Attvest or Victory funding statement here</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>or click to browse · .xlsx files only · reversals netted automatically</div>
          </>}
        </div>
      )}

      {preview && (
        <div style={{ background: 'var(--surface)', border: '1.5px solid rgba(59,130,246,0.3)', borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1.5px solid var(--border)', background: 'rgba(59,130,246,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>Preview — {preview.fileName}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Period: {preview.period} · {preview.items.length} policies · {fmtCurrDec(preview.totalAmount)} total
                {Object.entries(preview.funderSummary).map(([f, s]) => (<span key={f} style={{ marginLeft: 10, color: f.toLowerCase().includes('attvest') ? '#2563eb' : '#7c3aed', fontWeight: 600 }}>{f}: {s.count} · {fmtCurrDec(s.total)}</span>))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setPreview(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={savePreview} disabled={saving}>{saving ? 'Importing...' : 'Confirm Import'}</button>
            </div>
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 1fr 0.8fr 0.8fr', gap: 8, padding: '8px 20px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0 }}>
              {['Client', 'Funder', 'Premium', 'Commission', 'You Receive'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
            </div>
            {preview.items.map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 1fr 0.8fr 0.8fr', gap: 8, padding: '10px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{item.client_name}</div>
                <div><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: (item.funder || '').toLowerCase().includes('attvest') ? 'rgba(59,130,246,0.1)' : 'rgba(139,92,246,0.1)', color: (item.funder || '').toLowerCase().includes('attvest') ? '#2563eb' : '#7c3aed' }}>{item.funder}</span></div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{fmtCurrDec(item.premium_amount)}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{(item.commission_pct * 100).toFixed(1)}%</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>{fmtCurrDec(item.amount_paid)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? <div className="loading"><div className="spinner" /><span>Loading...</span></div>
        : remittances.length === 0 ? <div className="empty" style={{ padding: '28px 24px' }}><div className="empty-icon">💳</div><div className="empty-title">No funding statements yet</div><div className="empty-sub">Drop an Attvest or Victory Excel above to get started</div></div>
          : (
            <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr 2fr 100px', gap: 10, padding: '8px 20px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
                {['Period', 'Funder', 'Amount', 'Notes', ''].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
              </div>
              {remittances.map(r => (
                <div key={r.id}>
                  <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr 2fr 100px', gap: 10, padding: '14px 20px', borderBottom: '1.5px solid var(--border)', alignItems: 'center', cursor: 'pointer' }} onClick={() => loadItems(r.id)}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{r.period}</div>
                    <div>{(r.funder || '').split('+').map(f => f.trim()).map(f => <span key={f} style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, marginRight: 4, background: f.toLowerCase().includes('attvest') ? 'rgba(59,130,246,0.1)' : 'rgba(139,92,246,0.1)', color: f.toLowerCase().includes('attvest') ? '#2563eb' : '#7c3aed' }}>{f}</span>)}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#2563eb' }}>{fmtCurrDec(r.total_amount)}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.notes || '—'}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); loadItems(r.id) }}>{expandedId === r.id ? '▲' : '▼'}</button>
                      <button className="btn btn-danger btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); deleteRemittance(r.id) }}>✕</button>
                    </div>
                  </div>
                  {expandedId === r.id && itemsCache[r.id] && (
                    <div style={{ background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 1fr 0.8fr 0.8fr 0.8fr', gap: 8, padding: '6px 32px', borderBottom: '1px solid var(--border)' }}>
                        {['Client', 'Funder', 'Premium', 'Rate', 'Gross Comm', 'You Receive'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
                      </div>
                      {(itemsCache[r.id] || []).map(item => (
                        <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 1fr 0.8fr 0.8fr 0.8fr', gap: 8, padding: '8px 32px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{item.client_name}</div>
                          <div><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 8, background: (item.funder || '').toLowerCase().includes('attvest') ? 'rgba(59,130,246,0.1)' : 'rgba(139,92,246,0.1)', color: (item.funder || '').toLowerCase().includes('attvest') ? '#2563eb' : '#7c3aed' }}>{item.funder}</span></div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{fmtCurrDec(item.premium_amount)}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{item.commission_pct ? `${(item.commission_pct * 100).toFixed(1)}%` : '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{fmtCurrDec(item.commission_gross)}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>{fmtCurrDec(item.amount_paid)}</div>
                        </div>
                      ))}
                      {(itemsCache[r.id] || []).length === 0 && <div style={{ padding: '12px 32px', fontSize: 12, color: 'var(--muted)' }}>No line items</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
    </div>
  )
}

// ─── REMITTANCE TAB ───────────────────────────────────────────────────────────
function RemittanceTab({ showToast, coverConfirmations }) {
  const [remittances, setRemittances] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [preview, setPreview] = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [itemsCache, setItemsCache] = useState({})
  const [activeSection, setActiveSection] = useState('mclardy')

  useEffect(() => { load() }, [])

  const load = async () => {
    setLoading(true)
    const { data } = await supabase.from('remittances').select('*').order('payment_date', { ascending: false })
    setRemittances(data || [])
    setLoading(false)
  }

  const loadItems = async (id) => {
    if (expandedId === id) { setExpandedId(null); return }
    if (!itemsCache[id]) {
      const { data } = await supabase.from('remittance_items').select('*').eq('remittance_id', id).order('invoice_no')
      setItemsCache(prev => ({ ...prev, [id]: data || [] }))
    }
    setExpandedId(id)
  }

  const deleteRemittance = async (id) => {
    if (!window.confirm('Delete this remittance and all its line items?')) return
    await supabase.from('remittances').delete().eq('id', id)
    await load(); showToast('Deleted')
  }

  const parseExcel = async (file) => {
    setParsing(true)
    try {
      if (!window.XLSX) {
        const script = document.createElement('script')
        script.src = 'https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js'
        await new Promise((res, rej) => { script.onload = res; script.onerror = rej; document.head.appendChild(script) })
      }
      const XLSX = window.XLSX
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })

      const headerRowIdx = rows.findIndex(r => r && r.some(c => c && c.toString().includes('Invoice No')))
      if (headerRowIdx === -1) throw new Error('Could not find header row — is this a McLardy remittance Excel?')

      const headers = rows[headerRowIdx]
      const colIdx = (name) => headers.findIndex(h => h && h.toString().toLowerCase().includes(name.toLowerCase()))
      const invoiceCol = colIdx('Invoice No'), clientCol = colIdx('Client Name'), tranTypeCol = colIdx('Tran.Type')
      const policyCol = colIdx('Policy Number'), invoiceAmtCol = colIdx('Invoice Amount'), feeCol = colIdx('Broker Rep.Fee')
      const commCol = colIdx('Broker Rep.Commission'), netCol = colIdx('Net Amount Payable'), thisRemCol = colIdx('This Remittance'), classCol = colIdx('Class Of Business')
      const effectiveDateCol = colIdx('Effective Date')

      const totalRow = rows.find(r => r && r[policyCol] === 'Total:')
      const refRow = rows.find(r => r && r.some(c => c && c.toString().includes('Payment Tran.No')))
      const totalAmount = totalRow ? Number(totalRow[netCol]) : 0
      let tranNo = ''
      if (refRow) { const refIdx = refRow.findIndex(c => c && c.toString().includes('Payment Tran.No')); if (refIdx >= 0) tranNo = refRow[refIdx + 1]?.toString() || '' }

      const items = []
      for (let i = headerRowIdx + 1; i < rows.length; i++) {
        const r = rows[i]
        if (!r || !r[invoiceCol] || r[policyCol] === 'Total:') continue
        if (typeof r[invoiceCol] !== 'number') continue
        const policyNum = r[policyCol]?.toString() || ''
        const matched = (coverConfirmations || []).find(cc => {
          const crmPol = (cc.cover_data?.policy_number || '').toString().trim().toLowerCase()
          const remPol = (policyNum || '').toString().trim().toLowerCase()
          return crmPol && remPol && crmPol === remPol
        })
        items.push({
          invoice_no: r[invoiceCol]?.toString() || '', client_name: r[clientCol] || '', tran_type: r[tranTypeCol] || '',
          policy_number: policyNum, class_of_business: r[classCol] || '',
          effective_date: r[effectiveDateCol] ? new Date(r[effectiveDateCol]).toISOString().split('T')[0] : null,
          invoice_amount: Number(r[invoiceAmtCol]) || 0, broker_fee: Number(r[feeCol]) || 0,
          broker_commission: Number(r[commCol]) || 0, net_amount: (thisRemCol > -1 ? Number(r[thisRemCol]) : 0) || Number(r[netCol]) || 0,
          matched_cc_id: matched?.id || null, matched_client: matched ? (matched.cover_data?.insured_name || '') : null,
        })
      }
      const matchedCount = items.filter(i => i.matched_cc_id).length
      setPreview({ tranNo, paymentDate: new Date().toISOString().split('T')[0], totalAmount, items, fileName: file.name, matchedCount })
    } catch(e) { showToast('Parse error: ' + e.message) }
    setParsing(false)
  }

  const handleDrop = async (e) => {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (!file) return
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) return showToast('Please drop an Excel file (.xlsx)')
    await parseExcel(file)
  }
  const handleFileInput = async (e) => { const file = e.target.files[0]; if (!file) return; await parseExcel(file); e.target.value = '' }

  const savePreview = async () => {
    if (!preview) return
    setSaving(true)
    try {
      const { data: remData, error: remErr } = await supabase.from('remittances').insert({
        payment_date: preview.paymentDate, payment_reference: preview.tranNo || null, total_amount: preview.totalAmount, notes: `Imported from ${preview.fileName}`,
      }).select().single()
      if (remErr) throw new Error(remErr.message)
      if (preview.items.length > 0) {
        const { error: itemErr } = await supabase.from('remittance_items').insert(
          preview.items.map(item => ({ remittance_id: remData.id, invoice_no: item.invoice_no, client_name: item.client_name, tran_type: item.tran_type, policy_number: item.policy_number, class_of_business: item.class_of_business, effective_date: item.effective_date, invoice_amount: item.invoice_amount, broker_fee: item.broker_fee, broker_commission: item.broker_commission, net_amount: item.net_amount }))
        )
        if (itemErr) throw new Error(itemErr.message)
      }
      const matched = preview.items.filter(i => i.matched_cc_id)
      for (const item of matched) {
        const cc = (coverConfirmations || []).find(c => c.id === item.matched_cc_id)
        if (cc) {
          await supabase.from('cover_confirmations').update({ cover_data: { ...cc.cover_data, _remittance_paid: true, _remittance_tran: preview.tranNo, _actual_net_revenue: item.net_amount, _actual_broker_commission: item.broker_commission, _actual_broker_fee: item.broker_fee } }).eq('id', cc.id)
        }
      }
      await load(); setPreview(null)
      showToast(`Remittance imported — ${preview.items.length} policies, ${matched.length} matched to CRM`)
      setTimeout(() => window.location.reload(), 1500)
    } catch(e) { showToast('Save error: ' + e.message) }
    setSaving(false)
  }

  const reStampAll = async () => {
    if (!window.confirm('Re-run matching for all historical remittances? This will also backfill actual revenue figures onto any already-matched policies missing them.')) return
    setSaving(true)
    const { data: allItems } = await supabase.from('remittance_items').select('*')
    let stamped = 0, backfilled = 0
    for (const item of (allItems || [])) {
      const policyNum = (item.policy_number || '').toString().trim().toLowerCase()
      if (!policyNum) continue
      const matched = (coverConfirmations || []).find(cc => { const crmPol = (cc.cover_data?.policy_number || '').toString().trim().toLowerCase(); return crmPol && crmPol === policyNum })
      if (!matched) continue
      const alreadyPaid = matched.cover_data?._remittance_paid
      const missingActuals = matched.cover_data?._actual_net_revenue === undefined
      if (!alreadyPaid || missingActuals) {
        await supabase.from('cover_confirmations').update({ cover_data: { ...matched.cover_data, _remittance_paid: true, _remittance_tran: item.remittance_id, _actual_net_revenue: item.net_amount, _actual_broker_commission: item.broker_commission, _actual_broker_fee: item.broker_fee } }).eq('id', matched.id)
        if (!alreadyPaid) stamped++; else backfilled++
      }
    }
    setSaving(false)
    showToast(`✅ ${stamped} newly stamped as paid, ${backfilled} backfilled with actual revenue`)
    setTimeout(() => window.location.reload(), 1500)
  }

  const totalReceived = remittances.reduce((s, r) => s + (r.total_amount || 0), 0)
  const thisYearTotal = remittances.filter(r => r.payment_date?.startsWith(new Date().getFullYear().toString())).reduce((s, r) => s + (r.total_amount || 0), 0)

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setActiveSection('mclardy')} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid', borderColor: activeSection === 'mclardy' ? 'var(--forest)' : 'var(--border)', background: activeSection === 'mclardy' ? 'var(--forest)' : 'var(--surface2)', color: activeSection === 'mclardy' ? '#fff' : 'var(--muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>📋 McLardy Commission Remittances</button>
        <button onClick={() => setActiveSection('funding')} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid', borderColor: activeSection === 'funding' ? '#2563eb' : 'var(--border)', background: activeSection === 'funding' ? 'rgba(59,130,246,0.1)' : 'var(--surface2)', color: activeSection === 'funding' ? '#2563eb' : 'var(--muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>💳 Attvest & Victory Funding Statements</button>
      </div>

      {activeSection === 'funding' && <FundingRemittanceSection showToast={showToast} />}

      {activeSection === 'mclardy' && (
        <div>
          {!preview && (
            <div onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragOver(true) }} onDragLeave={() => setDragOver(false)} style={{ border: `2px dashed ${dragOver ? 'var(--forest)' : 'var(--border)'}`, borderRadius: 14, padding: '28px 20px', textAlign: 'center', marginBottom: 20, background: dragOver ? 'rgba(30,61,48,0.04)' : 'var(--surface)', cursor: 'pointer', transition: 'all .15s' }} onClick={() => document.getElementById('remittance-file-input').click()}>
              <input id="remittance-file-input" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileInput} />
              {parsing ? <div style={{ fontSize: 13, color: 'var(--muted)' }}>Parsing Excel file...</div> : <>
                <div style={{ fontSize: 24, marginBottom: 8 }}>📂</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Drop McLardy remittance Excel here</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>or click to browse · .xlsx files only · reconciles automatically with CRM</div>
              </>}
            </div>
          )}

          {preview && (
            <div style={{ background: 'var(--surface)', border: '1.5px solid var(--forest-border)', borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '14px 20px', borderBottom: '1.5px solid var(--border)', background: 'rgba(30,61,48,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>Preview — {preview.fileName}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Tran. #{preview.tranNo} · {preview.items.length} policies · {fmtCurrDec(preview.totalAmount)} total
                    <span style={{ marginLeft: 12, color: preview.matchedCount === preview.items.length ? 'var(--forest)' : '#7a5500', fontWeight: 600 }}>{preview.matchedCount}/{preview.items.length} matched to CRM</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPreview(null)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={savePreview} disabled={saving}>{saving ? 'Importing...' : 'Confirm Import'}</button>
                </div>
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.5fr 1fr 1fr 0.8fr 0.8fr', gap: 8, padding: '8px 20px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0 }}>
                  {['Invoice', 'Client', 'Policy', 'Class', 'Net Amount', 'CRM Match'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
                </div>
                {preview.items.map((item, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.5fr 1fr 1fr 0.8fr 0.8fr', gap: 8, padding: '10px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', background: item.matched_cc_id ? 'rgba(30,61,48,0.02)' : 'rgba(220,53,69,0.02)' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{item.invoice_no}</div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{item.client_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{item.policy_number}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{item.class_of_business}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurrDec(item.net_amount)}</div>
                    <div>{item.matched_cc_id ? <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--forest)', background: 'rgba(30,61,48,0.1)', padding: '2px 8px', borderRadius: 10 }}>✓ Matched</span> : <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--danger)', background: 'rgba(220,53,69,0.08)', padding: '2px 8px', borderRadius: 10 }}>No match</span>}</div>
                  </div>
                ))}
              </div>
              {preview.matchedCount < preview.items.length && (
                <div style={{ padding: '10px 20px', background: 'rgba(230,168,23,0.05)', borderTop: '1px solid rgba(230,168,23,0.2)', fontSize: 11, color: '#7a5500' }}>⚠️ {preview.items.length - preview.matchedCount} unmatched — may be from other AR groups or different policy numbers in CRM. Still saved to history.</div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-secondary btn-sm" onClick={reStampAll} disabled={saving} style={{ fontSize: 11 }}>🔄 Re-match all remittances to CRM</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <StatCard label="Total Received (All Time)" value={fmtCurrDec(totalReceived)} color="var(--forest)" />
            <StatCard label={`Received ${new Date().getFullYear()}`} value={fmtCurrDec(thisYearTotal)} color="var(--forest)" />
            <StatCard label="Payments Recorded" value={remittances.length} color="var(--forest)" />
          </div>

          {loading ? <div className="loading"><div className="spinner" /><span>Loading...</span></div>
            : remittances.length === 0 ? <div className="empty" style={{ padding: '32px 24px' }}><div className="empty-icon">💳</div><div className="empty-title">No remittances imported yet</div><div className="empty-sub">Drop a McLardy Excel above to get started</div></div>
              : (
                <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 120px', gap: 10, padding: '8px 20px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
                    {['Date', 'Reference', 'Amount', 'Notes', ''].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
                  </div>
                  {remittances.map(r => (
                    <div key={r.id}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 120px', gap: 10, padding: '14px 20px', borderBottom: '1.5px solid var(--border)', alignItems: 'center', cursor: 'pointer' }} onClick={() => loadItems(r.id)}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(r.payment_date)}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{r.payment_reference ? `Tran. #${r.payment_reference}` : '—'}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurrDec(r.total_amount)}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{r.notes || '—'}</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); loadItems(r.id) }}>{expandedId === r.id ? '▲' : '▼'}</button>
                          <button className="btn btn-danger btn-sm" style={{ fontSize: 10, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); deleteRemittance(r.id) }}>✕</button>
                        </div>
                      </div>
                      {expandedId === r.id && itemsCache[r.id] && (
                        <div style={{ background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.5fr 1fr 1fr 0.8fr', gap: 8, padding: '6px 32px', borderBottom: '1px solid var(--border)' }}>
                            {['Invoice', 'Client', 'Policy', 'Class', 'Net Amount'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
                          </div>
                          {itemsCache[r.id].map(item => (
                            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.5fr 1fr 1fr 0.8fr', gap: 8, padding: '8px 32px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{item.invoice_no}</div>
                              <div style={{ fontSize: 12 }}>{item.client_name}</div>
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{item.policy_number}</div>
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{item.class_of_business}</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurrDec(item.net_amount)}</div>
                            </div>
                          ))}
                          {itemsCache[r.id].length === 0 && <div style={{ padding: '12px 32px', fontSize: 12, color: 'var(--muted)' }}>No line items</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
        </div>
      )}
    </div>
  )
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export function Payments({ showToast, user, clients }) {
  const [activeTab, setActiveTab] = useState('overview')
  const [coverConfirmations, setCoverConfirmations] = useState([])
  const [fundingRemittances, setFundingRemittances] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    const [{ data }, { data: funding }] = await Promise.all([
      supabase.from('cover_confirmations').select('*').eq('status', 'Sent').order('created_at', { ascending: false }),
      supabase.from('funding_remittances').select('period,total_amount'),
    ])
    setCoverConfirmations(data || [])
    setFundingRemittances(funding || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const paymentDates = getPaymentDates()
  const nextPayment = paymentDates[0]

  const allPolicies = coverConfirmations.map(cc => {
    const d = cc.cover_data || {}
    const method = getPaymentMethod(d)
    const hasData = parseCurr(d.base_premium) > 0 || parseCurr(d.broker_fee) > 0
    const netRev = calcNetRev(d)
    const alreadyPaid = d._remittance_paid === true
    const paidManually = d._paid_manually === true
    const startDate = d.period_from ? new Date(d.period_from) : null
    let clearanceDate = null
    if (hasData && !alreadyPaid) {
      if ((method === 'attvest' || method === 'victory') && startDate) clearanceDate = addBusinessDays(startDate, method === 'attvest' ? ATTVEST_DAYS : VICTORY_DAYS)
      else if (method === 'bpay' && paidManually) clearanceDate = new Date()
    }
    const cycleIndex = clearanceDate ? assignToCycle(clearanceDate, paymentDates) : null
    return { method, netRev, hasData, cycleIndex, alreadyPaid, paidManually, isCancelled: !!d._cancelled }
  })

  const nextCycleRevenue = allPolicies.filter(p => p.hasData && !p.isCancelled && p.cycleIndex === 0).reduce((s, p) => s + p.netRev, 0)
  const bpayCount = allPolicies.filter(p => p.method === 'bpay' && !p.alreadyPaid && !p.paidManually && !p.isCancelled).length

  // Funding expected THIS calendar month (estimate) — clearance-date bucketed, base_premium × rate × 1.045
  const fundingExpectedThisMonth = coverConfirmations.reduce((s, cc) => {
    const d = cc.cover_data || {}
    if (d._cancelled) return s
    const method = getPaymentMethod(d)
    if (method !== 'attvest' && method !== 'victory') return s
    if (parseCurr(d.base_premium) <= 0) return s
    const start = d.period_from ? new Date(d.period_from) : null
    if (!start || isNaN(start)) return s
    const clearance = addBusinessDays(start, method === 'attvest' ? ATTVEST_DAYS : VICTORY_DAYS)
    const now = new Date()
    if (clearance.getFullYear() === now.getFullYear() && clearance.getMonth() === now.getMonth()) s += calcFundingIncome(d)
    return s
  }, 0)

  // Funding actually received this calendar month / all-time
  const fundingReceivedThisMonth = fundingRemittances.filter(r => isPeriodThisMonth(r.period)).reduce((s, r) => s + (r.total_amount || 0), 0)
  const fundingReceivedAllTime = fundingRemittances.reduce((s, r) => s + (r.total_amount || 0), 0)

  const tabs = [
    { key: 'overview', label: '🏠 Overview', count: null },
    { key: 'forecast', label: '📈 Commission Forecast', count: null },
    { key: 'debtors', label: '⚡ Debtor Follow-Up', count: bpayCount > 0 ? bpayCount : null },
    { key: 'expenses', label: '🧾 Expenses', count: null },
    { key: 'remittances', label: '💳 Remittance History', count: null },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#1e3d30,#2a5240)', borderRadius: 16, padding: '24px 28px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 6 }}>Next McLardy Payment</div>
            <div style={{ fontSize: 30, fontWeight: 700, color: '#fff' }}>{nextPayment ? nextPayment.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>Cutoff: {nextPayment ? getCutoff(nextPayment).toLocaleDateString('en-AU', { day: 'numeric', month: 'long' }) : '—'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, auto)', gap: '18px 34px' }}>
            <HeaderStat label="Commission this cycle" value={fmtCurrDec(nextCycleRevenue)} sub="clears into next payment" color="#4ade80" estimate />
            <HeaderStat label="BPAY to chase" value={bpayCount} sub={bpayCount === 1 ? '1 client unpaid' : `${bpayCount} clients unpaid`} color={bpayCount > 0 ? '#e6a817' : 'rgba(255,255,255,0.9)'} />
            <HeaderStat label="Funding due this month" value={fmtCurrDec(fundingExpectedThisMonth)} sub="forecast" color="#93c5fd" estimate />
            <HeaderStat label="Funding received this month" value={fmtCurrDec(fundingReceivedThisMonth)} sub="banked" color="#fff" />
          </div>
        </div>
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: 24, fontSize: 12, color: 'rgba(255,255,255,0.6)', flexWrap: 'wrap' }}>
          <span>Funding received all-time: <strong style={{ color: 'rgba(255,255,255,0.95)' }}>{fmtCurrDec(fundingReceivedAllTime)}</strong></span>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
          <span>Dashed / <span style={{ color: '#ffd27a' }}>EST</span> = forecast · white = money banked</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1.5px solid var(--border)', flexWrap: 'wrap' }}>
        {tabs.map(({ key, label, count }) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{ padding: '8px 18px', background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === key ? 'var(--forest)' : 'transparent'}`, fontWeight: activeTab === key ? 700 : 500, color: activeTab === key ? 'var(--forest)' : 'var(--muted)', fontSize: 13, cursor: 'pointer', marginBottom: -1.5, fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 6 }}>
            {label}
            {count && <span style={{ background: 'var(--orange)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>{count}</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="loading"><div className="spinner" /><span>Loading...</span></div> : (
        <>
          {activeTab === 'overview' && <OverviewTab coverConfirmations={coverConfirmations} />}
          {activeTab === 'forecast' && <ForecastTab coverConfirmations={coverConfirmations} clients={clients} showToast={showToast} />}
          {activeTab === 'debtors' && <DebtorTab coverConfirmations={coverConfirmations} clients={clients} showToast={showToast} user={user} onRefresh={load} />}
          {activeTab === 'expenses' && <ExpensesTab user={user} showToast={showToast} />}
          {activeTab === 'remittances' && <RemittanceTab showToast={showToast} coverConfirmations={coverConfirmations} />}
        </>
      )}
    </div>
  )
}
